<div class="dialog-header">
    <h2 mat-dialog-title>Add Cashflow</h2>
    <button mat-icon-button mat-dialog-close class="dialog-close" aria-label="Close" data-test="dialog-close">
        <mat-icon>close</mat-icon>
    </button>
</div>
<mat-dialog-content>
  <form [formGroup]="cashflowForm">
    <div>
      @if (hasDuplicates) {
        <valumize-info-panel [appearance]="'ERROR'"
        [message]="'One or more cashflow entries overlap with an existing cashflow of the same type and date'"></valumize-info-panel>
      }
      <div class="form-fields">
        <mat-form-field>
          <mat-label>Cashflow Type</mat-label>
          <mat-select formControlName="cashflowType">
            @for (code of filterAssetCashflowTypes(selectCodesCashflowType$ | async); track code) {
              <mat-option [value]="code.code">
                {{code.translations?.["en"]}}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="fill">
          <mat-label>Cashflow Date</mat-label>
          <input matInput
            formControlName="cashflowDate"
            id="cashflowDate"
            [matDatepicker]="cashflowDatePicker">
          <mat-hint>dd/MM/yyyy</mat-hint>
          <mat-datepicker-toggle matIconSuffix [for]="cashflowDatePicker"></mat-datepicker-toggle>
          <mat-datepicker #cashflowDatePicker></mat-datepicker>
        </mat-form-field>
        <span><mat-checkbox color="primary" formControlName="recurringCashflow"></mat-checkbox><label>Recurring Cashflow</label></span>
      </div>
      @if (cashflowForm.get('recurringCashflow')?.value) {
        <div class="recurring-box">
          <h3>Recurring Cashflow Options</h3>
          <div class="form-fields">
            <div>
              <mat-form-field>
                <mat-label>Repeat every</mat-label>
                <mat-select formControlName="recurringFrequency">
                  <mat-option [value]="'month'">Month</mat-option>
                  <mat-option [value]="'quarter'">Quarter</mat-option>
                  <mat-option [value]="'semester'">Semester</mat-option>
                  <mat-option [value]="'year'">Year</mat-option>
                </mat-select>
              </mat-form-field>
            </div>
            <mat-radio-group class="recurring-options" formControlName="recurringEndCondition">
              <div class="form-fields">
                <mat-radio-button color="primary" value="occurrences">End after number of occurrences</mat-radio-button>
                @if (cashflowForm.get('recurringEndCondition')?.value === 'occurrences') {
                  <mat-form-field class="input-number-narrow">
                    <input matInput type="number" formControlName="numberOfOccurrences" min="1">
                  </mat-form-field>
                }
              </div>
              <div class="form-fields">
                <mat-radio-button color="primary" value="date">End on specific date</mat-radio-button>
                @if (cashflowForm.get('recurringEndCondition')?.value === 'date') {
                  <mat-form-field appearance="fill" class="input-date-narrow">
                    <input matInput formControlName="recurringEndDate" id="recurringEndDate" [matDatepicker]="recurringEndDatePicker">
                    <mat-hint>dd/MM/yyyy</mat-hint>
                    <mat-datepicker-toggle matIconSuffix [for]="recurringEndDatePicker"></mat-datepicker-toggle>
                    <mat-datepicker #recurringEndDatePicker></mat-datepicker>
                  </mat-form-field>
                }
              </div>
              <mat-radio-button color="primary" value="exit">End on exit date</mat-radio-button>
            </mat-radio-group>
          </div>
          <div class="form-fields">
            @if (recurringEndDateError) {
              <div class="form-fields, form-error-message">
                Recurring end date must be after the report date {{ recurringEndDateControl?.getError('dateAfter')?.requiredDate | date }}.
              </div>
            }
            @if (numberOfOccurrencesError) {
              <div class="form-fields, form-error-message">
                Number of occurrences must be between 1 and 99.
              </div>
            }
          </div>
        </div>
      }
      <mat-radio-group formControlName="selectedRowIndex">
        <table mat-table [dataSource]="rows.controls" formArrayName="rows">
          @if (isValuationTypeDmv()) {
            <ng-container matColumnDef="select">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
                <mat-radio-button
                  color="primary"
                  [value]="i"
                  (change)="onRowSelect(i)">
                </mat-radio-button>
              </td>
            </ng-container>
          }

          <ng-container matColumnDef="type">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              <div>{{rows.at(i).get('type')?.value}}</div>
            </td>
          </ng-container>

          <ng-container matColumnDef="low">
            <th mat-header-cell *matHeaderCellDef>Low</th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              @if (isInjectionCashflow()) {
                <span class="minus">-</span>
              }
              <input matInput formControlName="low" type="number" class="input-number-narrow"/>
            </td>
          </ng-container>

          <ng-container matColumnDef="base">
            <th mat-header-cell *matHeaderCellDef>Base</th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              @if (isInjectionCashflow()) {
                <span class="minus">-</span>
              }
              <input matInput formControlName="base" type="number" class="input-number-narrow"/>
            </td>
          </ng-container>

          <ng-container matColumnDef="high">
            <th mat-header-cell *matHeaderCellDef>High</th>
            <td mat-cell *matCellDef="let row; let i = index" [formGroupName]="i">
              @if (isInjectionCashflow()) {
                <span class="minus">-</span>
              }
              <input matInput formControlName="high" type="number" class="input-number-narrow"/>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="columns"></tr>
          <tr mat-row *matRowDef="let row; columns: columns"></tr>
        </table>
      </mat-radio-group>
    </div>
  </form>
</mat-dialog-content>
<mat-dialog-actions align="end">
  @if (assetValuation && scenarios) {
    <button mat-stroked-button color="primary" [disabled]="cashflowForm.invalid" (click)="createCashflows(assetValuation, scenarios)">Add Cashflows</button>
  }
</mat-dialog-actions>
